import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Trash2 } from 'lucide-react';
import { TaskImage } from '../types/Task';
import { taskApi } from '../services/api';
import './ImageUpload.css';

interface ImageUploadProps {
  taskId?: string;
  images?: TaskImage[];
  onImagesChange?: (images: TaskImage[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  maxFileSize?: number; // in MB
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  taskId,
  images = [],
  onImagesChange,
  disabled = false,
  maxFiles = 10,
  maxFileSize = 10
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debug logging
  useEffect(() => {
    console.log('🔧 ImageUpload component rendered with:', {
      taskId,
      disabled,
      imagesCount: images.length,
      selectedFilesCount: selectedFiles.length
    });
  }, [taskId, disabled, images.length, selectedFiles.length]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`);
        return false;
      }

      // Check file size
      if (file.size > maxFileSize * 1024 * 1024) {
        alert(`${file.name} is too large (max ${maxFileSize}MB)`);
        return false;
      }

      return true;
    });

    // Check total file count
    if (images.length + selectedFiles.length + validFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} images allowed`);
      return;
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (!taskId || selectedFiles.length === 0) return;

    console.log('🚀 Starting image upload...');
    console.log('📋 Task ID:', taskId);
    console.log('📁 Files to upload:', selectedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));

    setUploading(true);
    try {
      const result = await taskApi.uploadImages(taskId, selectedFiles);
      console.log('✅ Upload completed successfully:', result);
      
      if (result.errors && result.errors.length > 0) {
        console.warn('⚠️ Some files had errors:', result.errors);
        alert(`Some files failed to upload:\n${result.errors.join('\n')}`);
      }

      // Update the images list
      if (onImagesChange && result.images) {
        console.log('🔄 Updating images list with:', result.images);
        onImagesChange([...images, ...result.images]);
      }

      // Clear selected files
      setSelectedFiles([]);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error: any) {
      console.error('❌ Upload failed:', error);
      console.error('📄 Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      alert('Failed to upload images: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imageId: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await taskApi.deleteImage(imageId);
      
      // Update the images list
      if (onImagesChange) {
        onImagesChange(images.filter(img => img.id !== imageId));
      }
    } catch (error: any) {
      console.error('Delete failed:', error);
      alert('Failed to delete image: ' + (error.response?.data?.error || error.message));
    }
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="image-upload">
      <div className="image-upload-header">
        <h4>
          <ImageIcon size={16} />
          Attached Images ({images.length})
        </h4>
      </div>

      {/* Existing Images */}
      {images.length > 0 && (
        <div className="existing-images">
          {images.map((image) => (
            <div key={image.id} className="image-item">
              <img
                src={taskApi.getImageUrl(image.id)}
                alt={image.original_name}
                className="image-thumbnail"
                onClick={() => window.open(taskApi.getImageUrl(image.id), '_blank')}
              />
              <div className="image-info">
                <span className="image-name">{image.original_name}</span>
                <span className="image-size">{(image.file_size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
              {!disabled && (
                <button
                  className="delete-image-btn"
                  onClick={() => deleteImage(image.id)}
                  title="Delete image"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {!disabled && (
        <>
          <div
            className={`upload-area ${dragOver ? 'drag-over' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={openFileDialog}
          >
            <Upload size={24} />
            <p>Drop images here or click to select</p>
            <p className="upload-hint">
              Max {maxFiles} files, {maxFileSize}MB each
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h5>Selected Files ({selectedFiles.length})</h5>
              {selectedFiles.map((file, index) => (
                <div key={index} className="selected-file">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                  <button
                    className="remove-file-btn"
                    onClick={() => removeSelectedFile(index)}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              
              <div className="upload-actions">
                <button
                  className="upload-btn"
                  onClick={() => {
                    console.log('🖱️ Upload button clicked!', {
                      taskId,
                      selectedFilesCount: selectedFiles.length,
                      uploading,
                      disabled
                    });
                    uploadFiles();
                  }}
                  disabled={uploading || !taskId}
                >
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} file(s)`}
                </button>
                <button
                  className="cancel-btn"
                  onClick={() => setSelectedFiles([])}
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImageUpload; 